/**
 * Utility functions for constructing and parsing authentication domain URLs
 */

export interface AuthTokens {
    accessToken: string;
    refreshToken: string;
}

export interface AuthCallbackResult {
    token: string | null;
    refresh: string | null;
    success: boolean;
    error: string | null;
}

/**
 * Constructs a full login domain URL with return URL and optional invite code
 * @param authPath - The path on the login domain (e.g., "/login", "/registration")
 * @param returnUrl - The full URL to return to after authentication
 * @param invite - Optional invite code for registration
 * @returns Full login domain URL with query parameters
 */
export const constructAuthUrl = (authPath: string, returnUrl: string, invite?: string): string => {
    const authDomain = getAuthDomain();
    const url = new URL(authPath, authDomain);

    url.searchParams.set("returnUrl", returnUrl);

    if (invite) {
        url.searchParams.set("invite", invite);
    }

    return url.toString();
};

/**
 * Extracts the return URL from URLSearchParams
 * @param searchParams - URL search parameters
 * @returns The return URL or null if not found
 */
export const parseReturnUrl = (searchParams: URLSearchParams): string | null => {
    return searchParams.get("returnUrl");
};

/**
 * Constructs a callback URL with authentication tokens
 * @param returnUrl - The tenant domain URL to redirect to
 * @param tokens - Access and refresh tokens, or null if authentication failed
 * @param success - Whether authentication was successful
 * @param error - Optional error message if authentication failed
 * @param email - Optional user email for OAuth flows
 * @param invite - Optional invite code for OAuth flows
 * @param firebaseUid - Optional Firebase user ID for OAuth flows
 * @returns Full callback URL with query parameters
 */
export const constructCallbackUrl = (
    returnUrl: string,
    tokens: AuthTokens | null,
    success: boolean,
    error?: string,
    email?: string,
    invite?: string | null,
    firebaseUid?: string
): string => {
    try {
        const url = new URL(returnUrl);

        // Ensure the path includes /auth/callback
        if (!url.pathname.includes("/auth/callback")) {
            url.pathname = "/auth/callback";
        }

        url.searchParams.set("success", success.toString());

        if (success && tokens) {
            url.searchParams.set("token", tokens.accessToken);
            url.searchParams.set("refresh", tokens.refreshToken);
        }

        if (error) {
            url.searchParams.set("error", error);
        }

        // Add OAuth-specific parameters
        if (email) {
            url.searchParams.set("email", email);
            url.searchParams.set("isOAuth", "true");
        }

        if (invite) {
            url.searchParams.set("invite", invite);
        }

        if (firebaseUid) {
            url.searchParams.set("firebaseUid", firebaseUid);
        }

        return url.toString();
    } catch (err) {
        console.error("Error constructing callback URL:", err);
        throw new Error("Invalid return URL");
    }
};

/**
 * Parses authentication callback parameters from URL search params
 * @param searchParams - URL search parameters
 * @returns Parsed authentication result
 */
export const parseAuthCallback = (searchParams: URLSearchParams): AuthCallbackResult => {
    return {
        token: searchParams.get("token"),
        refresh: searchParams.get("refresh"),
        success: searchParams.get("success") === "true",
        error: searchParams.get("error")
    };
};

/**
 * Gets the login domain URL based on current environment
 * @returns Full login domain URL (e.g., "https://mylogin.dev.kenbakke.com")
 */
const getAuthDomain = (): string => {
    const hostname = window.location.hostname;

    // Handle localhost development
    if (hostname === "localhost" || hostname === "127.0.0.1") {
        return `http://localhost:${window.location.port || 3000}`;
    }

    const loginDomain = process.env.REACT_APP_LOGIN_DOMAIN;
    if (!loginDomain) {
        throw new Error("REACT_APP_LOGIN_DOMAIN environment variable not set");
    }

    return loginDomain;
};

/**
 * Extracts the main domain from a hostname
 * @param hostname - The hostname to parse (e.g., "trial.kenbakke.com")
 * @returns The main domain (e.g., "kenbakke.com")
 */
export const extractMainDomainFromHostname = (hostname: string): string => {
    const parts = hostname.split(".");

    // Handle localhost
    if (hostname === "localhost" || hostname === "127.0.0.1") {
        return "localhost";
    }

    // Return last two parts (e.g., "kenbakke.com" from "trial.kenbakke.com")
    return parts.slice(-2).join(".");
};
